import type { WizardStep } from './wizardSteps';

const SIDE_PANEL = 'flex shrink-0 flex-col overflow-hidden rounded-lg bg-background';

/**
 * The wizard's panel classes, read by both `ReelsWizardShell` and `ReelsWizardSkeleton`: the
 * skeleton is replaced by the shell without a shift only while the two share these sizes.
 */
export const WIZARD_PANELS = {
  body: 'flex min-h-0 flex-1',
  /** The space the column keeps from the stage, which the shell's reveal animates as its margin. */
  columnSlot: 'flex shrink-0',
  column: `${SIDE_PANEL} h-full w-102`,
  columnHeader:
    'flex min-h-16 shrink-0 items-center gap-xs border-b border-border py-sm pr-xl pl-sm',
  stepsStrip: 'flex shrink-0 items-center justify-center border-b border-border px-lg py-sm',
  stepBubble: 'flex items-center gap-xs rounded-lg px-2xs py-xs',
  stepNumber: 'size-6 shrink-0 rounded-sm',
  stepSeparator: 'mx-2xs h-px w-3.5 bg-border',
  columnBody: 'flex min-h-0 flex-1 flex-col',
  /** The main pane and the right panel, whose width the right panel's opening does not change. */
  stage: 'flex min-h-0 min-w-0 flex-1',
  main: 'relative flex h-full min-w-0 flex-1 flex-col overflow-hidden rounded-lg bg-background',
  mainBar:
    'grid min-h-16 shrink-0 grid-cols-[1fr_minmax(0,auto)_1fr] items-center gap-md border-b border-border px-md py-sm',
  mainScroll: 'min-h-0 flex-1 overflow-y-auto',
  asideSlot: 'flex shrink-0',
  aside: `${SIDE_PANEL} h-full w-70`,
} as const;

/** The gutter between panels, `gap-app-inset`, which a reveal keeps as its own margin. */
const PANEL_GAP_PX = 8;

/** The right panel's open width with its gutter: `w-70` and `PANEL_GAP_PX`. */
export const ASIDE_RESERVE_PX = 280 + PANEL_GAP_PX;

/** The main bar's height, `min-h-16`. */
export const MAIN_BAR_PX = 64;

/** The Studio's own layout: a header card over Segment Details, the player and Source Media. */
export const STUDIO_PANELS = {
  body: 'flex min-h-0 flex-1 flex-col gap-app-inset',
  header: 'shrink-0 rounded-lg bg-background',
  row: 'flex min-h-0 flex-1 gap-app-inset',
  column: `${SIDE_PANEL} w-83`,
  main: 'flex h-full min-w-0 flex-1 flex-col',
  aside: `${SIDE_PANEL} w-80`,
} as const;

/** The steps whose screen has a panel right of the main pane: the script and storyboard once a scene is in view. */
export const WIZARD_STEPS_WITH_ASIDE: readonly WizardStep[] = ['script', 'storyboard'];
